import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthConfig } from "./shared";

/*
 * Laboratoire — Traçabilité / journal d'audit (loi 18-07 / 25-11), DEDIE.
 *
 * Port de AuditModule (HopitalDashboard.js), repointé sur
 * /api/lab/audit/logs au lieu de /api/sih/audit/logs (voir
 * backend/routes/lab/audit.py, fork en lecture seule de
 * GET /api/sih/audit/logs). db.audit_logs est déjà rempli pour un
 * laboratoire : chaque route labo déjà livrée appelle déjà
 * utils.write_audit_log(...) à chaque action sensible.
 */
const AUDIT_ACTION_COLORS = {
  create: ["#EAF3DE", "#3B6D11"],
  update: ["#E0E7FF", "#3730A3"],
  delete: ["#FCEBEB", "#A32D2D"],
  read:   ["#F1F5F9", "#64748B"],
  login:  ["#DBEAFE", "#1E40AF"],
  assign: ["#FEF3C7", "#854F0B"],
  denied: ["#FCEBEB", "#A32D2D"],
  upload: ["#E0E7FF", "#3730A3"],
};

export default function AuditModule({ can }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [fAction, setFAction] = useState("");
  const [fEntity, setFEntity] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 100;
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fAction) params.append("action", fAction);
      if (fEntity) params.append("entity_type", fEntity);
      if (fFrom) params.append("date_from", fFrom);
      if (fTo) params.append("date_to", fTo);
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      const r = await axios.get(`${API_URL}/api/lab/audit/logs?${params.toString()}`, getAuthConfig());
      setLogs(r.data.logs || []); setTotal(r.data.total || 0);
      setActions(r.data.actions || []); setEntityTypes(r.data.entity_types || []);
    } catch (e) { setError("Impossible de charger le journal d'audit"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [fAction, fEntity, fFrom, fTo, page]);
  const iS = { padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR"); } catch (e) { return iso; } };
  const resetFilters = () => { setFAction(""); setFEntity(""); setFFrom(""); setFTo(""); setPage(0); };
  const totalPages = Math.ceil(total / pageSize);
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Tracabilite</h1>
      <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>Journal d'audit — lecture seule (conforme loi 18-07 / 25-11)</div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16 }}>
        <div><label style={lab}>Action</label><select value={fAction} onChange={(e) => { setFAction(e.target.value); setPage(0); }} style={iS}><option value="">Toutes</option>{actions.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
        <div><label style={lab}>Type d'objet</label><select value={fEntity} onChange={(e) => { setFEntity(e.target.value); setPage(0); }} style={iS}><option value="">Tous</option>{entityTypes.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
        <div><label style={lab}>Du</label><input type="date" value={fFrom} onChange={(e) => { setFFrom(e.target.value); setPage(0); }} style={iS} /></div>
        <div><label style={lab}>Au</label><input type="date" value={fTo} onChange={(e) => { setFTo(e.target.value); setPage(0); }} style={iS} /></div>
        {(fAction || fEntity || fFrom || fTo) && <button onClick={resetFilters} style={{ padding: "8px 14px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Reinitialiser</button>}
        <div style={{ marginLeft: "auto", fontSize: 13, color: "#64748B", alignSelf: "center" }}>{total} evenement(s)</div>
      </div>
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Date/heure</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Utilisateur</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Role</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Action</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Objet</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Reference</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>IP</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Chargement...</td></tr>}
              {!loading && logs.length === 0 && <tr><td colSpan={7} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun evenement.</td></tr>}
              {!loading && logs.map((l, i) => { const ac = AUDIT_ACTION_COLORS[l.action] || ["#F1F5F9", "#64748B"]; return (
                <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "10px 14px", fontSize: 13, color: "#334155", whiteSpace: "nowrap" }}>{fmt(l.ts)}</td>
                  <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500 }}>{l.user_name || "—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: "#64748B" }}>{l.user_role || "—"}</td>
                  <td style={{ padding: "10px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: ac[0], color: ac[1] }}>{l.action}</span></td>
                  <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{l.entity_type || "—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: "#94A3B8", fontFamily: "monospace" }}>{l.entity_id || "—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: "#94A3B8" }}>{l.ip || "—"}</td>
                </tr>); })}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 16 }}>
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} style={{ padding: "8px 14px", background: "#fff", color: page === 0 ? "#CBD5E1" : "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, cursor: page === 0 ? "not-allowed" : "pointer" }}>Precedent</button>
          <span style={{ fontSize: 13, color: "#64748B" }}>Page {page + 1} / {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} style={{ padding: "8px 14px", background: "#fff", color: page >= totalPages - 1 ? "#CBD5E1" : "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, cursor: page >= totalPages - 1 ? "not-allowed" : "pointer" }}>Suivant</button>
        </div>
      )}
    </div>
  );
}
